import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { customersApi, type Customer360 } from '../../api/customers.api.js';
import { useAuth } from '../../auth/useAuth.js';
import { BulkDocumentUpload } from '../../components/BulkDocumentUpload.js';
import { CustomerDocumentDialog, ValidityChip } from '../../components/CustomerDocumentDialog.js';
import { documentTypeLabel } from '../../components/document-types.js';
import { StatusBadge } from '../../components/StatusBadge.js';

import { CustomerKycReports } from './CustomerKycReports.js';
import { DetailField, WorkspaceSectionHeader } from './CustomerWorkspace.js';
import { APP_LOCALE } from '../../utils/locale.js';

export function CustomerKycTab({ customer, canReports, canReadOnboarding, retryBusy, onRetry }: {
  customer: Customer360; canReports: boolean; canReadOnboarding: boolean; retryBusy: boolean; onRetry: () => void;
}): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // The same rights the server applies; it decides for real.
  const mayHandle = user?.role === 'admin' || user?.role === 'compliance';
  const [action, setAction] = useState<{ kind: 'rename' | 'remove'; id: string } | null>(null);
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [removeKind, setRemoveKind] = useState('DUPLICATE');
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState('');
  const [viewing, setViewing] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const business = customer.accountType === 'BUSINESS' || (customer.accountType === undefined && customer.business !== null);
  return <Stack spacing={3}>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: canReports ? 'repeat(2,minmax(0,1fr))' : 'minmax(0,1fr)' }, gap: 3, alignItems: 'start' }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
        <Typography variant="h6" component="h3">Review details</Typography>
        <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 2.5, mb: 0 }}>
          <DetailField label="Status"><StatusBadge kind="kyc" value={customer.kyc.status} /></DetailField>
          <DetailField label="Risk rating"><StatusBadge kind="risk" value={customer.kyc.riskRating} /></DetailField>
          <DetailField label="Approved">{customer.approval ? `${new Date(customer.approval.approvedAt).toLocaleDateString(APP_LOCALE)}${customer.approval.approvedBy ? ` · ${customer.approval.approvedBy}` : ''}` : '—'}</DetailField>
          <DetailField label="Last review">{customer.kyc.lastReviewDate ? new Date(customer.kyc.lastReviewDate).toLocaleDateString(APP_LOCALE) : '—'}</DetailField>
          <DetailField label="EDD">{customer.kyc.eddStatus ?? 'N/A'}</DetailField>
          {business && customer.business ? <>
            <DetailField label="Legal name">{customer.business.legalName}</DetailField>
            <DetailField label="Registration">{customer.business.registrationNumber} ({customer.business.incorporationCountry})</DetailField>
            <DetailField label="Company registry check">{customer.business.registryVerificationStatus}</DetailField>
          </> : null}
        </Box>
        {business && customer.business && customer.business.registryVerificationStatus !== 'VERIFIED' ? <Alert severity="info" sx={{ mt: 2.5 }}>
          The company register has not been independently checked. Verify the entity against the national register before approving.
        </Alert> : null}
      </Paper>
      {canReports ? <Paper sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
        <CustomerKycReports key={customer.customerId} customerId={customer.customerId} isBusiness={business} />
      </Paper> : null}
    </Box>
    <Paper sx={{ overflow: 'hidden', minWidth: 0 }}>
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3 }}>
        <WorkspaceSectionHeader title="Documents on file" actions={<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          {canReadOnboarding && customer.applicationId ?
            <Button size="small" className="workspace-link" component={RouterLink} to={`/onboarding-applications/${customer.applicationId}`}>Open the onboarding record</Button> : null}
          {mayHandle ? <BulkDocumentUpload customerId={customer.customerId} isBusiness={business} onUploaded={onRetry} /> : null}
        </Stack>} />
        {canReadOnboarding && !customer.applicationId ? customer.applicationId === null ?
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No onboarding record is linked to this customer.</Typography> :
          <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Onboarding record unavailable.</Typography>
            <Button size="small" disabled={retryBusy} onClick={onRetry}>Retry onboarding lookup</Button>
          </Stack> : null}
      </Box>
      {/* Filter by type (DEV-98): a business file holds a dozen documents of
          five kinds, and the officer usually wants one kind. A filter nobody
          has documents under is not offered. */}
      {(customer.documents ?? []).length > 0 ? (() => {
        const counts = new Map<string, number>();
        for (const d of customer.documents ?? []) counts.set(d.documentType, (counts.get(d.documentType) ?? 0) + 1);
        return counts.size > 1 ? (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" role="group" aria-label="Filter documents by type" sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
            <Chip size="small" label={`All · ${String(customer.documents?.length ?? 0)}`} color={typeFilter === null ? 'primary' : 'default'}
              variant={typeFilter === null ? 'filled' : 'outlined'} onClick={() => setTypeFilter(null)} aria-pressed={typeFilter === null} />
            {[...counts.entries()].sort((a, b) => documentTypeLabel(a[0]).localeCompare(documentTypeLabel(b[0]))).map(([type, n]) => (
              <Chip key={type} size="small" label={`${documentTypeLabel(type)} · ${String(n)}`} color={typeFilter === type ? 'primary' : 'default'}
                variant={typeFilter === type ? 'filled' : 'outlined'} onClick={() => setTypeFilter(typeFilter === type ? null : type)} aria-pressed={typeFilter === type} />
            ))}
          </Stack>
        ) : null;
      })() : null}
      {(customer.documents ?? []).length === 0 ? <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 3 }}>No documents are recorded against this client.</Typography> :
        <TableContainer><Table size="small" aria-label="Customer documents" sx={{ minWidth: 580 }}>
          <TableHead><TableRow><TableCell>Document</TableCell><TableCell>Status</TableCell><TableCell>Validity</TableCell><TableCell>Collected</TableCell>{mayHandle ? <TableCell /> : null}</TableRow></TableHead>
          <TableBody>{customer.documents?.filter((document) => typeFilter === null || document.documentType === typeFilter).map((document) => <TableRow key={document.id} hover>
            <TableCell>
              {/* Opening a file is compliance's right (the server enforces it). */}
              {mayHandle ? <Button size="small" onClick={() => setViewing(document.id)} sx={{ p: 0, minWidth: 0, textTransform: 'none', textAlign: 'left', justifyContent: 'flex-start', fontWeight: 500 }}>
                {document.name ?? documentTypeLabel(document.documentType)}
              </Button> : (document.name ?? documentTypeLabel(document.documentType))}
              {document.name ? <Typography variant="caption" color="text.secondary" display="block">{documentTypeLabel(document.documentType)}</Typography> : null}
            </TableCell>
            <TableCell>{document.status}</TableCell>
            <TableCell><Tooltip title={document.validityDetail}><span><ValidityChip validity={document.validity} /></span></Tooltip></TableCell>
            <TableCell>{new Date(document.collectedAt).toLocaleDateString(APP_LOCALE)}</TableCell>
            {mayHandle ? <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              <Button size="small" onClick={() => setViewing(document.id)}>View</Button>
              {/* Only a vault file has a name and bytes to act on. */}
              {document.origin === 'VAULT' ? <>
                <Button size="small" onClick={() => { setAction({ kind: 'rename', id: document.id }); setName(document.name ?? ''); setActionError(''); }}>Rename</Button>
                <Button size="small" color="error" onClick={() => { setAction({ kind: 'remove', id: document.id }); setRemoveKind('DUPLICATE'); setReason(''); setActionError(''); }}>Remove</Button>
              </> : null}
            </TableCell> : null}
          </TableRow>)}</TableBody>
        </Table></TableContainer>}

      {(() => {
        const open = (customer.documents ?? []).find((d) => d.id === viewing);
        return open === undefined ? null : (
          <CustomerDocumentDialog key={open.id} customerId={customer.customerId} document={open} isBusiness={business}
            canEdit={mayHandle} onClose={() => setViewing(null)} onChanged={onRetry} />
        );
      })()}

      <Dialog open={action !== null} onClose={() => setAction(null)} fullWidth maxWidth="sm">
        <DialogTitle>{action?.kind === 'rename' ? 'Rename this document' : 'Remove this document'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {actionError !== '' ? <Alert severity="error">{actionError}</Alert> : null}
            {action?.kind === 'rename' ? <TextField autoFocus label="Name" value={name} onChange={(e) => setName(e.target.value)}
              inputProps={{ maxLength: 200 }} helperText="The name it was uploaded under is kept in the history." /> : <>
              <Typography variant="body2">It leaves the working lists. The file itself is kept, and the removal is recorded on the client&rsquo;s file with your name and reason.</Typography>
              <TextField select label="What is wrong with it" value={removeKind} onChange={(e) => setRemoveKind(e.target.value)}>
                <MenuItem value="DUPLICATE">A duplicate</MenuItem>
                <MenuItem value="WRONG_TYPE">The wrong kind of document</MenuItem>
                {isAdmin ? <MenuItem value="WRONG_CLIENT">Filed against the wrong client</MenuItem> : null}
                <MenuItem value="OTHER">Something else</MenuItem>
              </TextField>
              <TextField label="Why" value={reason} onChange={(e) => setReason(e.target.value)} multiline minRows={2} required inputProps={{ maxLength: 1000 }} />
            </>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)} disabled={working}>Cancel</Button>
          <Button variant="contained" color={action?.kind === 'remove' ? 'error' : 'primary'}
            disabled={working || (action?.kind === 'rename' ? name.trim().length < 2 : reason.trim().length < 4)}
            onClick={() => {
              if (action === null) return;
              setWorking(true);
              setActionError('');
              const done = action.kind === 'rename'
                ? customersApi.renameDocument(customer.customerId, action.id, name.trim())
                : customersApi.removeDocument(customer.customerId, action.id, removeKind, reason.trim());
              done.then(() => { setAction(null); onRetry(); })
                .catch((err: unknown) => setActionError(err instanceof Error ? err.message : 'That could not be saved.'))
                .finally(() => setWorking(false));
            }}>
            {action?.kind === 'rename' ? 'Rename' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  </Stack>;
}
